import { ReactComponent as DownIcon } from "../assets/icons/down-arrow.svg";
import { ReactComponent as PlusIcon } from "../assets/icons/add.svg";
import "../stylesheets/productMenu.scss";

// TYPES

import { ChangeEvent, useEffect, useState } from "react";
import {
	addProduct,
	AddProductType,
	Category,
	getFeatured,
	getMenu,
	Menu,
	ProductType,
	Subcategory,
} from "../services/api";
import ProductContainer from "./ProductContainer";
import Product from "./Product";
import { isEditor } from "../services/utils";
import AddProductForm from "./AddProductForm";
import EditButton from "./EditButton";
import { useNotification } from "../contexts/NotificationContext";

// CONSTS

// COMPONENT

function ProductMenu() {
	// STATES
	const [menu, setMenu] = useState<Menu | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [selected, setSelected] = useState<string>("Cocktails");
	const [products, setProducts] = useState<ProductType[]>([]);
	const [isAdding, setAdding] = useState<boolean>(false);
	const [category, setCategory] = useState<string>("Cocktails");
	const [subcategory, setSubcategory] = useState<string | undefined>(
		undefined,
	);
	const [batchUpload, setBatchUpload] = useState<string | undefined>(
		undefined,
	);
	const [featuredProducts, setFeaturedProducts] = useState<string[]>([]);
	const { notify } = useNotification();

	// USE EFFECTS
	useEffect(() => {
		const fetchMenu = async () => {
			const menu = await getMenu();
			const featured = (await getFeatured()).products.map(
				(product) => product.id,
			);
			setFeaturedProducts(featured);
			setMenu(menu);
			setIsLoading(false);
			if (menu.categories != undefined) {
				const cat = menu.categories.find(
					(category) => category.label == selected,
				);
				if (cat) {
					setProducts(
						cat.products
							.sort((p1: ProductType, p2: ProductType) => {
								if (p1.index && p2.index) {
									return p1.index > p2.index
										? 1
										: p1.index == p2.index
											? 0
											: -1;
								}
								return 0;
							})
							.map((p) => ({ ...p })),
					);
				}
			}
		};
		fetchMenu();
	}, []);
	// HANDLES
	const handleProductAdded = (newProduct: ProductType) => {
		setProducts((prev) => [...prev, newProduct]);
		setAdding(false);
	};
	const handleCategorySelection = (label: string) => {
		setCategory(label);
		setSubcategory(undefined);
		const category: Category | undefined = menu?.categories.find(
			(category) => category.label == label,
		);
		if (category) {
			if (category.subcategories.length == 0) {
				document
					.querySelectorAll(".subcategories")
					.forEach((subcategory) =>
						subcategory.classList.add("hidden"),
					);
				setProducts(category.products.map((p) => ({ ...p })));
				setSelected(category.label);
			} else {
				const subcategories = document.getElementById(
					"subcategory-" + label,
				);
				if (subcategories?.classList.contains("hidden")) {
					subcategories.classList.remove("hidden");
				} else {
					subcategories?.classList.add("hidden");
				}
			}
		}
	};
	const handleSubcategorySelection = (
		categoryLabel: string,
		SubcategoryLabel: string,
	) => {
		const category: Category | undefined = menu?.categories.find(
			(category) => category.label == categoryLabel,
		);
		document
			.querySelectorAll(".subcategories")
			.forEach((subcategory) => subcategory.classList.add("hidden"));
		if (category) {
			const subcategory: Subcategory | undefined =
				category.subcategories.find(
					(subcategory) => subcategory.label == SubcategoryLabel,
				);
			if (subcategory) {
				setProducts(subcategory.products.map((p) => ({ ...p })));
				setSelected(subcategory.label);
				setSubcategory(subcategory.label);
			}
		}
	};
	const handleMoveUp = (index: number) => {
		if (index === 0) return;
		const updated = [...products];
		[updated[index - 1], updated[index]] = [
			updated[index],
			updated[index - 1],
		];
		setProducts(updated);
	};

	const handleMoveDown = (index: number) => {
		if (index === products.length - 1) return;
		const updated = [...products];
		[updated[index], updated[index + 1]] = [
			updated[index + 1],
			updated[index],
		];
		setProducts(updated);
	};

	const handleEdit = (
		id: string,
		updated: {
			name: string;
			price: number;
			ingredients: string[];
			featured: boolean;
		},
	) => {
		setProducts((prev) =>
			prev.map((p) => (p.id === id ? { ...p, ...updated } : p)),
		);
		if (updated.featured) {
			setFeaturedProducts(featuredProducts.concat([id]));
		} else {
			setFeaturedProducts(featuredProducts.filter((pid) => pid === id));
		}
	};
	const handleSave = (data: object | null, filename: string) => {
		if (!data) {
			throw new Error("data is null");
		}
		const blob = new Blob([JSON.stringify(data, null, 2)], {
			type: "application/json",
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	};
	async function handleMenuUpload(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		try {
			const text = await file.text();
			const menu: Menu = JSON.parse(text);

			const promises: Promise<ProductType>[] = [];

			for (const category of menu.categories) {
				for (const product of category.products) {
					promises.push(
						addProduct({
							name: product.name,
							price: product.price,
							ingredients: product.ingredients,
							categoryLabel: category.label,
							featured: false,
						}),
					);
				}

				for (const subcategory of category.subcategories) {
					for (const product of subcategory.products) {
						promises.push(
							addProduct({
								name: product.name,
								price: product.price,
								ingredients: product.ingredients,
								categoryLabel: category.label,
								subcategoryLabel: subcategory.label,
								featured: false,
							}),
						);
					}
				}
			}

			const results = await Promise.allSettled(promises);
			const failed = results.filter(
				(r) => r.status === "rejected",
			).length;

			notify({
				type: "validation",
				title: `Produit(s) Ajouté(s) ${results.length - failed}/${results.length}`,
			});
		} catch {
			notify({
				type: "error",
				title: "Erreur lors de l'upload du menu",
			});
		}

		e.target.value = "";
	}

	// TSX
	return (
		<div className="product-menu">
			<img src={"/assets/menu/" + selected.toLowerCase() + ".jpg"} />
			<div>
				<div className="categories">
					{menu?.categories.map((category) => (
						<div
							key={category.label}
							onClick={() =>
								handleCategorySelection(category.label)
							}
							className="category"
						>
							<span className="primary-text">
								{category.label}
							</span>
							{category.subcategories.length > 0 && (
								<>
									<DownIcon />
									<div
										id={"subcategory-" + category.label}
										className="subcategories hidden"
									>
										{category.subcategories.map(
											(subcategory) => (
												<button
													key={subcategory.label}
													onClick={(e) => {
														e.stopPropagation();
														handleSubcategorySelection(
															category.label,
															subcategory.label,
														);
													}}
												>
													{subcategory.label}
												</button>
											),
										)}
									</div>
								</>
							)}
						</div>
					))}
				</div>
				<ProductContainer>
					{products.map((product, index) => (
						<Product
							key={product.name}
							name={product.name}
							ingredients={product.ingredients}
							price={product.price}
							id={product.id}
							isFeatured={
								featuredProducts.find(
									(id) => id === product.id,
								) != undefined
							}
							onMoveUp={() => handleMoveUp(index)}
							onMoveDown={() => handleMoveDown(index)}
							onEdit={(updated) =>
								handleEdit(product.id, updated)
							}
						/>
					))}
				</ProductContainer>
				{isEditor() && (
					<div id="menu-controls">
						<EditButton
							type="button"
							variant="download"
							onClick={() => handleSave(menu, "menu.json")}
						/>
						<EditButton
							type="button"
							variant="add"
							onClick={() => setAdding(!isAdding)}
						/>
						<EditButton
							type="file"
							variant="upload"
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								handleMenuUpload(e)
							}
						/>
					</div>
				)}
			</div>
			{isAdding && (
				<AddProductForm
					key={`${category}-${subcategory ?? ""}`}
					onClose={() => setAdding(false)}
					category={category}
					onProductAdded={handleProductAdded}
					subcategory={subcategory}
				/>
			)}
		</div>
	);
}

export default ProductMenu;
